import { ApiResponse } from '@magnetar/admin-api-interfaces'
import { useAuth } from './useAuth'
import { useGet } from './useFetch'

// 小時記錄類型
interface PatientFlowHourlyRecord {
  flow_hourly_id: number
  clinic_id: number
  snapshot_hour: string
  patient_count: number
  created_at: string
  updated_at: string
}

// 每日記錄類型
interface PatientFlowDailyRecord {
  flow_daily_id: number
  clinic_id: number
  date: string
  patient_count: number
  created_at: string
  updated_at: string
}

// 通用記錄類型（根據 interval 參數決定返回哪種類型）
type PatientFlowRecord = PatientFlowHourlyRecord | PatientFlowDailyRecord

interface PatientFlowRecordsData {
  records: PatientFlowRecord[]
  total: number
}

export interface UseClinicPatientFlowRecordsParams {
  clinicId?: number
  page?: number
  pageSize?: number
  startDate?: string
  endDate?: string
  date?: string // 單日查詢（用於 hourly interval）
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
  interval?: 'hourly' | 'daily'
}

export function useClinicPatientFlowRecords({
  clinicId,
  page = 1,
  pageSize = 10,
  startDate,
  endDate,
  date,
  sortBy,
  sortOrder = 'desc',
  interval = 'daily',
}: UseClinicPatientFlowRecordsParams = {}) {
  const { token } = useAuth()

  // Build query string
  const queryParams = new URLSearchParams()

  if (interval === 'hourly') {
    // hourly 端點使用 date 單日查詢
    if (date) queryParams.append('date', date)
  } else {
    // daily 端點使用 startDate/endDate 範圍查詢
    queryParams.append('page', page.toString())
    queryParams.append('pageSize', pageSize.toString())
    if (startDate) queryParams.append('startDate', startDate)
    if (endDate) queryParams.append('endDate', endDate)
    if (sortBy) queryParams.append('sortBy', sortBy)
    if (sortOrder) queryParams.append('sortOrder', sortOrder)
  }

  const endpoint = interval === 'hourly' ? 'hourly' : 'daily'

  // hourly 需要 date 參數才發請求
  const shouldFetch =
    token && clinicId && (interval === 'daily' || (interval === 'hourly' && date))

  const { data, error, mutate, isLoading } = useGet<ApiResponse<PatientFlowRecordsData>>(
    shouldFetch
      ? `/api/clinic-patient-flow/${clinicId}/${endpoint}?${queryParams.toString()}`
      : null,
    undefined,
    token ?? '',
  )

  return {
    patientFlowRecords: data?.data?.records || [],
    total: data?.data?.total || 0,
    isLoading,
    isError: error,
    mutate,
  }
}
